import React, { Suspense, lazy, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./firebase/firebase";
import TopBar from "./components/TopBar";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import LayoutContainer from "./components/LayoutContainer";
import BackToTop from "./components/BackToTop";
import BannerSlider from "./components/BannerSlider";
import FixedSidebars from "./components/FixedSidebars";
import Loader from "./components/Loader";

// Lazy load public pages
const HomePage = lazy(() => import("./pages/HomePage"));
const AboutHistory = lazy(() => import("./pages/AboutHistory"));
const AboutVisionMission = lazy(() => import("./pages/AboutVisionMission"));
const AboutCorrespondentMessage = lazy(() => import("./pages/AboutCorrespondentMessage"));
const AboutPrincipalMessage = lazy(() => import("./pages/AboutPrincipalMessage"));
const AdmissionProcess = lazy(() => import("./pages/AdmissionProcess"));
const AdmissionRules = lazy(() => import("./pages/AdmissionRules"));
const AdmissionScholarship = lazy(() => import("./pages/AdmissionScholarship"));
const AcademicsKindergarten = lazy(() => import("./pages/AcademicsKindergarten"));
const AcademicsSecondary = lazy(() => import("./pages/AcademicsSecondary"));
const AdministrationsTeachingStaff = lazy(() => import("./pages/AdministrationsTeachingStaff"));
const AdministrationsNonTeachingStaff = lazy(() => import("./pages/AdministrationsNonTeachingStaff"));
const FacilitiesLab = lazy(() => import("./pages/FacilitiesLab"));
const FacilitiesLibrary = lazy(() => import("./pages/FacilitiesLibrary"));
const FacilitiesTransport = lazy(() => import("./pages/FacilitiesTransport"));
const ActivitiesScout = lazy(() => import("./pages/ActivitiesScout"));
const ActivitiesJuniorRedCross = lazy(() => import("./pages/ActivitiesJuniorRedCross"));
const Alumni = lazy(() => import("./pages/Alumni"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));

// Lazy load admin pages
const AdminLogin = lazy(() => import("./admin/Login"));
const AdminDashboard = lazy(() => import("./admin/Dashboard"));
const AdminBanner = lazy(() => import("./admin/Banner"));
const AdminAchievements = lazy(() => import("./admin/Achievements"));
const AdminNews = lazy(() => import("./admin/News"));
const AdminEvents = lazy(() => import("./admin/Events"));
const AdminHighlights = lazy(() => import("./admin/Highlights"));
const AdminAdmissions = lazy(() => import("./admin/Admissions"));
const AdminAlumni = lazy(() => import("./admin/Alumni"));
const AdminScholarship = lazy(() => import("./admin/Scholarship"));
const AdminGallery = lazy(() => import("./admin/Gallery"));
const AdminUpcoming = lazy(() => import("./admin/Upcoming"));

// ProtectedRoute for admin
function ProtectedRoute({ children }) {
  const [user, loading] = useAuthState(auth);
  const [adminLoggedIn, setAdminLoggedIn] = React.useState(false);

  React.useEffect(() => {
    const adminAuth = localStorage.getItem("adminLoggedIn");
    if (adminAuth) {
      setAdminLoggedIn(true);
    }
  }, []);

  if (loading) return <Loader />;
  return user || adminLoggedIn ? children : <Navigate to="/admin/login" replace />;
}

// Layout for public pages
function PublicLayout() {
  const location = useLocation();
  const [formType, setFormType] = useState(null);

  const handleFormOpen = (type) => {
    setFormType(type);
    if (type === "scholarship-inside" || type === "scholarship-outside") {
      window.location.href = "/admission/scholarship";
    } else if (type === "alumni") {
      window.location.href = "/alumni";
    } else if (type === "admission") {
      window.location.href = "/admission/process";
    }
  };

  return (
    <LayoutContainer>
      <TopBar />
      <Navbar />
      <FixedSidebars onFormOpen={handleFormOpen} />
      {/* Render banner only on homepage */}
      {location && location.pathname === "/" && <BannerSlider />}
      <Outlet />
      <Footer />
      <BackToTop />
    </LayoutContainer>
  );
}

const App = () => (
  <Router>
    <Suspense fallback={<Loader />}>
      <Routes>
        {/* Public routes wrapped in LayoutContainer */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about/history" element={<AboutHistory />} />
          <Route path="/about/vision-mission" element={<AboutVisionMission />} />
          <Route path="/about/correspondent-message" element={<AboutCorrespondentMessage />} />
          <Route path="/about/principal-message" element={<AboutPrincipalMessage />} />
          <Route path="/admission/process" element={<AdmissionProcess />} />
          <Route path="/admission/rules" element={<AdmissionRules />} />
          <Route path="/admission/scholarship" element={<AdmissionScholarship />} />
          <Route path="/academics/kindergarten" element={<AcademicsKindergarten />} />
          <Route path="/academics/secondary" element={<AcademicsSecondary />} />
          <Route path="/administrations/teaching-staff" element={<AdministrationsTeachingStaff />} />
          <Route path="/administrations/non-teaching-staff" element={<AdministrationsNonTeachingStaff />} />
          <Route path="/facilities/lab" element={<FacilitiesLab />} />
          <Route path="/facilities/library" element={<FacilitiesLibrary />} />
          <Route path="/facilities/transport" element={<FacilitiesTransport />} />
          <Route path="/activities/scout" element={<ActivitiesScout />} />
          <Route path="/activities/junior-red-cross" element={<ActivitiesJuniorRedCross />} />
          <Route path="/alumni" element={<Alumni />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
        </Route>
        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/banner" element={<ProtectedRoute><AdminBanner /></ProtectedRoute>} />
        <Route path="/admin/achievements" element={<ProtectedRoute><AdminAchievements /></ProtectedRoute>} />
        <Route path="/admin/news" element={<ProtectedRoute><AdminNews /></ProtectedRoute>} />
        <Route path="/admin/events" element={<ProtectedRoute><AdminEvents /></ProtectedRoute>} />
        <Route path="/admin/highlights" element={<ProtectedRoute><AdminHighlights /></ProtectedRoute>} />
        <Route path="/admin/admissions" element={<ProtectedRoute><AdminAdmissions /></ProtectedRoute>} />
        <Route path="/admin/alumni" element={<ProtectedRoute><AdminAlumni /></ProtectedRoute>} />
        <Route path="/admin/scholarship" element={<ProtectedRoute><AdminScholarship /></ProtectedRoute>} />
        <Route path="/admin/gallery" element={<ProtectedRoute><AdminGallery /></ProtectedRoute>} />
        <Route path="/admin/upcoming" element={<ProtectedRoute><AdminUpcoming /></ProtectedRoute>} />
      </Routes>
    </Suspense>
  </Router>
);

export default App;
